import { useMemo, type CSSProperties } from "react";

interface Props {
  /** Global stage energy in [0,1] — the mean of the four stem envelopes. */
  energy: number;
  /** Hex of the mood light — tints the crowd glow and the hype meter. */
  moodHex: string;
  /** Whether the transport is playing. Stopped → still, dim crowd. */
  playing: boolean;
}

/** One audience silhouette — a pixel-art head + shoulders, LPC-flavored. */
function Head({ x, shade }: { x: number; shade: string }): JSX.Element {
  return (
    <g>
      {/* shoulders */}
      <rect x={x - 3.5} y={11} width={7} height={5} fill={shade} />
      {/* head */}
      <rect x={x - 2.5} y={5} width={5} height={6} fill={shade} />
      {/* hair cap — a touch darker */}
      <rect x={x - 2.5} y={5} width={5} height={1.6} fill="rgba(0,0,0,0.45)" />
    </g>
  );
}

/**
 * A reactive pixel-art audience on the front lip of the `LiveStage`
 * (Plan 13, Fase 6, spec §8.2).
 *
 * - High energy → the crowd rows bounce, amplitude scaled by `--energy`.
 * - Low energy / idle → the bounce settles and the lighters/phone-screens
 *   flicker as static points of light (`crowd-flicker`).
 * - The "hype meter" — a thin bar pinned bottom-right — integrates the global
 *   energy and is tinted by the mood color.
 *
 * Visual language stays in the project's warm DAW palette (`tape` / `vinyl` /
 * `butter`, asphalt floor) — no dark/teal; the spec's mention of teal was a
 * stale note. Pure presentational component: energy comes from `useStageEnergy`.
 */
export function CrowdLayer({ energy, moodHex, playing }: Props): JSX.Element {
  // Three staggered rows of heads — front row darkest, back row faintest, so
  // the crowd reads with depth.
  const rows = useMemo(
    () => [
      { y: 30, count: 13, shade: "#0c0907", spread: 7.7 },
      { y: 24, count: 11, shade: "#171009", spread: 9.1 },
      { y: 19, count: 9, shade: "#241910", spread: 11.1 },
    ],
    [],
  );

  // Lighters: scattered glints whose count grows with calm; brightest when the
  // crowd is swaying gently rather than jumping.
  const lighters = useMemo(
    () => [12, 27, 41, 58, 70, 84].map((x, i) => ({ x, y: 14 + (i % 3) * 4, delay: i * 0.7 })),
    [],
  );

  // Bounce amplitude is gated so a stopped transport shows a still crowd.
  const bounceEnergy = playing ? energy : 0;
  const hypePct = Math.round(Math.min(1, Math.max(0, energy)) * 100);

  const rowStyle = (delay: number): CSSProperties => ({
    ["--energy" as string]: bounceEnergy.toFixed(3),
    animation: playing ? `crowd-bounce ${0.34 + delay}s ease-in-out infinite` : undefined,
    transformBox: "fill-box",
  });

  return (
    <div
      data-testid="crowd-layer"
      className="pointer-events-none absolute inset-x-0 bottom-0 z-[5]"
      style={{ height: 56 }}
    >
      {/* Crowd glow — a warm wash from the stage edge, tinted by the mood */}
      <div
        className="absolute inset-x-0 bottom-0"
        style={{
          height: 56,
          background: `linear-gradient(180deg, ${moodHex}00 0%, ${moodHex}${playing ? "26" : "10"} 100%)`,
          transition: "background 200ms linear",
        }}
      />

      <svg
        className="absolute inset-x-0 bottom-0 h-full w-full"
        viewBox="0 0 100 36"
        preserveAspectRatio="xMidYMax slice"
        shapeRendering="crispEdges"
      >
        {/* Lighters / phone screens — flicker, brighter when the crowd is calm */}
        <g opacity={playing ? 1 - bounceEnergy * 0.6 : 0.5}>
          {lighters.map((l) => (
            <g key={`l-${l.x}`}>
              <circle
                cx={l.x}
                cy={l.y}
                r={1.5}
                fill={moodHex}
                style={{
                  animation: playing
                    ? `crowd-flicker ${2.2 + (l.delay % 1.5)}s ease-in-out ${l.delay}s infinite`
                    : undefined,
                }}
              />
              <circle cx={l.x} cy={l.y} r={0.6} fill="#fff8dc" />
            </g>
          ))}
        </g>

        {/* Crowd rows — back to front so nearer rows occlude farther ones */}
        {rows.map((row, ri) => (
          <g key={`row-${row.y}`} style={rowStyle(ri * 0.06)}>
            {Array.from({ length: row.count }).map((_, i) => (
              <Head key={i} x={4 + i * row.spread} shade={row.shade} />
            ))}
          </g>
        ))}
      </svg>

      {/* Hype meter — a slim integrator bar, mood-tinted */}
      <div className="absolute bottom-2 right-3 flex items-center gap-1.5">
        <span className="font-mono text-[8px] uppercase tracking-widest2 text-ink-muted">hype</span>
        <div
          className="relative h-1.5 w-20 border border-border bg-asphalt/80"
          role="meter"
          aria-label="Medidor de hype da plateia"
          aria-valuenow={hypePct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${hypePct}%`,
              background: `linear-gradient(90deg, ${moodHex}99, ${moodHex})`,
              boxShadow: `0 0 6px -1px ${moodHex}`,
              transition: "width 110ms linear",
            }}
          />
        </div>
      </div>
    </div>
  );
}
